import { useEffect, useState } from "react"
import { toast } from "sonner"
import { cx } from "@/lib/utils/cx"
import { ApiError } from "@/lib/api/client"
import {
  activationApi,
  type ActivationView,
  type BookingSlot,
} from "@/lib/api/onboarding-activation"
import { PrimaryButton, StageHeader, ActionRow, focusRing, sectionLabelClass } from "./ui"

// Stage 5 — book the installation call. Two taps: a day, a time. Day tiles
// read like calendar pages; the button names the exact slot it books.
export function BookingStep({
  token,
  data,
  onBack,
  onBooked,
}: {
  token: string
  data: ActivationView
  onBack: () => void
  onBooked: (next: ActivationView) => void
}) {
  const [slots, setSlots] = useState<BookingSlot[]>([])
  const [loading, setLoading] = useState(true)
  const [dayIdx, setDayIdx] = useState(0)
  const [time, setTime] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let alive = true
    activationApi
      .getSlots(token)
      .then((r) => alive && setSlots(r.slots))
      .catch(() => {})
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [token])

  const day = slots[dayIdx]
  const shortDay = day
    ? new Date(day.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" })
    : ""

  const book = async () => {
    if (!day || !time || submitting) return
    setSubmitting(true)
    try {
      onBooked(await activationApi.book(token, { date: day.date, time }))
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't book that time — try another slot.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <StageHeader
        stage="Step 5 of 5"
        title="Choose your installation call time"
        lede={`Pick a day, then pick a time. It's a 45-minute video call with ${data.salesRep.name} and an implementation specialist — account setup, your data connection, and advisor training.`}
      />

      {loading ? (
        <div className="grid place-items-center py-16">
          <span className="size-6 animate-spin rounded-full border-2 border-[#DCE4DF] border-t-emerald-600" />
        </div>
      ) : (
        <>
          <p className={cx("mb-2", sectionLabelClass)}>Pick a day</p>
          <div className="-mx-1 flex gap-2.5 overflow-x-auto px-1 pb-1">
            {slots.map((s, i) => {
              const d = new Date(s.date + "T00:00:00")
              const active = i === dayIdx
              return (
                <button
                  key={s.date}
                  onClick={() => {
                    setDayIdx(i)
                    setTime(null)
                  }}
                  aria-pressed={active}
                  className={cx(
                    "flex w-[76px] shrink-0 flex-col items-center rounded-2xl border py-2.5 transition-all duration-150",
                    focusRing,
                    active
                      ? "border-emerald-500 bg-emerald-50/70 ring-4 ring-emerald-500/10"
                      : "border-[#DCE4DF] bg-white hover:border-emerald-300",
                  )}
                >
                  <span
                    className={cx(
                      "text-[10px] font-bold uppercase tracking-wide",
                      active ? "text-emerald-700" : "text-[#9AA8A0]",
                    )}
                  >
                    {d.toLocaleDateString("en-US", { weekday: "short" })}
                  </span>
                  <span
                    className={cx(
                      "mt-0.5 text-[20px] font-bold leading-none tracking-tight",
                      active ? "text-[#0C2018]" : "text-[#5A6A61]",
                    )}
                  >
                    {d.getDate()}
                  </span>
                  <span className="mt-0.5 text-[10.5px] font-medium text-[#9AA8A0]">
                    {d.toLocaleDateString("en-US", { month: "short" })}
                  </span>
                </button>
              )
            })}
          </div>

          <p className={cx("mb-2 mt-4", sectionLabelClass)}>Pick a time</p>
          <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-5">
            {day?.times.map((t) => (
              <button
                key={t}
                onClick={() => setTime(t)}
                aria-pressed={time === t}
                className={cx(
                  "rounded-xl border py-3 text-[13.5px] font-semibold transition-all duration-150",
                  focusRing,
                  time === t
                    ? "border-emerald-600 bg-emerald-600 text-white shadow-lg shadow-emerald-600/25"
                    : "border-[#DCE4DF] bg-white text-[#5A6A61] hover:border-emerald-300",
                )}
              >
                {t}
              </button>
            ))}
          </div>
          <p className="mt-2.5 text-[12px] text-[#9AA8A0]">Times are shown in your local time zone.</p>

          <ActionRow back={onBack}>
            <PrimaryButton onClick={book} disabled={!time} loading={submitting}>
              {time ? `Book ${shortDay}, ${time}` : "Pick a time to book"}
            </PrimaryButton>
          </ActionRow>
        </>
      )}
    </div>
  )
}
